import { useCallback, useMemo } from 'react';
import { useSelector } from 'react-redux';
import { useSearchParams } from 'react-router';

import { selectIsUserSuperAdminOrAdmin } from 'src/reducers/userReducer/userReducer';
import { ChannelAccessLevel, SolveChannel } from 'src/services/apiInterfaces';
import { useGetChannelAccessQuery } from 'src/services/dashboard-api';
import { useGetOrgConfigQuery } from 'src/services/settings/settingsApi';
import {
  channelForProduct,
  conversationChannelToSolveChannel,
  meetsLevel,
  TAB_CHANNEL_TO_SOLVE_CHANNEL,
} from 'src/utils/channelAccess';

export const CHANNEL_SCOPED_PERMISSIONS_FLAG = 'channel_scoped_permissions';

export const useIsChannelScopedPermissionsEnabled = (): boolean => {
  const { data: orgConfig } = useGetOrgConfigQuery();
  return Boolean(
    orgConfig?.feature_flags?.includes(CHANNEL_SCOPED_PERMISSIONS_FLAG),
  );
};

/** Grants drive access only when `hasGrants` is true; otherwise callers keep today's role checks. */
export const useChannelAccess = () => {
  const { data, isLoading: isQueryLoading } = useGetChannelAccessQuery();
  // Orgs without the flag never wait on channel access.
  const isLoading = useIsChannelScopedPermissionsEnabled() && isQueryLoading;
  const hasGrants = Boolean(data?.has_grants);
  const hasFullAccess = Boolean(data?.has_full_access);
  const canEditAnywhere = Object.values(data?.levels ?? {}).some(level =>
    meetsLevel(level, 'edit'),
  );
  // Org-wide builder settings (actions, context variables, custom events) need edit on one channel.
  const canEditOrgBuilderSettings = !hasGrants || canEditAnywhere;

  const levelFor = useCallback(
    (channel: SolveChannel | undefined): ChannelAccessLevel =>
      (channel && data?.levels[channel]) || 'none',
    [data],
  );

  const visibleChannels = useMemo(
    () =>
      new Set(
        (Object.keys(data?.levels ?? {}) as SolveChannel[]).filter(channel =>
          meetsLevel(data?.levels[channel], 'view'),
        ),
      ),
    [data],
  );

  // What the org's plan enables, from the endpoint rather than other users' rows.
  // An absent entitled_channels (deploy skew with the endpoint change) fails open,
  // like the users-scan this replaced.
  const unentitledChannels = useMemo(() => {
    const entitled = data?.entitled_channels;
    if (!entitled) {
      return new Set<SolveChannel>();
    }
    const entitledSet = new Set(entitled);
    return new Set(
      (Object.keys(data?.levels ?? {}) as SolveChannel[]).filter(
        channel => !entitledSet.has(channel),
      ),
    );
  }, [data]);

  return {
    canEditAnywhere,
    canEditOrgBuilderSettings,
    hasFullAccess,
    hasGrants,
    isLoaded: Boolean(data),
    isLoading,
    levelFor,
    levels: (data?.levels ?? {}) as Partial<
      Record<SolveChannel, ChannelAccessLevel>
    >,
    unentitledChannels,
    visibleChannels,
  };
};

/** Users whose access comes from channel grants need edit on at least one channel to change actions. */
export const useCanCreateActions = (): boolean => {
  const isAdmin = useSelector(selectIsUserSuperAdminOrAdmin);
  const { canEditAnywhere, hasGrants } = useChannelAccess();
  return isAdmin || !hasGrants || canEditAnywhere;
};

/** Context variables are admin-only; a USER with role-level full Agent Builder access also qualifies. Mirrors the server's dep_full_access. */
export const useCanEditContextVariables = (): boolean => {
  const isAdmin = useSelector(selectIsUserSuperAdminOrAdmin);
  const { hasFullAccess } = useChannelAccess();
  return isAdmin || hasFullAccess;
};

/** Writing a conversation's human-feedback review takes edit on its channel; granted users with only view get the read-only verdict card. */
export const useCanEditConversationReview = (
  conversationChannel: string | null | undefined,
): boolean => {
  const isAdmin = useSelector(selectIsUserSuperAdminOrAdmin);
  const { hasGrants, levelFor } = useChannelAccess();
  if (isAdmin || !hasGrants) {
    return true;
  }
  return meetsLevel(
    levelFor(conversationChannelToSolveChannel(conversationChannel)),
    'edit',
  );
};

/** Edit/publish rights for the workflow open in the builder, from its `view` param. */
export const useCurrentWorkflowAccess = () => {
  const [searchParams] = useSearchParams();
  const { hasGrants, levelFor } = useChannelAccess();
  const level = levelFor(
    TAB_CHANNEL_TO_SOLVE_CHANNEL[searchParams.get('view') ?? 'widget'],
  );
  return {
    canEdit: !hasGrants || meetsLevel(level, 'edit'),
    canPublish: !hasGrants || meetsLevel(level, 'publish'),
  };
};

/**
 * Publish right for a workflow-scoped or org-wide item (knowledge coaching,
 * content snippets): publish on the item's channel, or on any channel when the
 * item isn't tied to one. Callers pass the item's workflow product, if known.
 */
export const useCanPublishForProduct = (
  product: string | null | undefined,
): boolean => {
  const { hasGrants, isLoading, levels } = useChannelAccess();
  if (!hasGrants || isLoading) {
    return true;
  }
  const channel = channelForProduct(product);
  if (channel) {
    return meetsLevel(levels[channel], 'publish');
  }
  return Object.values(levels).some(level => meetsLevel(level, 'publish'));
};

/** Publish on at least one channel; non-granted users keep today's role-based access. */
export const useCanPublishAnyChannel = (): boolean =>
  useCanPublishForProduct(undefined);
